import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Loader2, LogOut, Camera, Save, User, Phone, Globe2,
  MapPin, Briefcase, Calendar, Flag, FileText, Mail, Shield, Check
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/api";
import { errMsg } from "@/utils";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

const FIELD_DEFS = [
  { key: "name",         label: "Full Name",          type: "text",  icon: User,      required: true },
  { key: "phone",        label: "Phone Number",        type: "tel",   icon: Phone },
  { key: "country",      label: "Country of Residence",type: "text",  icon: Globe2 },
  { key: "nationality",  label: "Nationality",         type: "text",  icon: Flag },
  { key: "date_of_birth",label: "Date of Birth",       type: "date",  icon: Calendar },
  { key: "occupation",   label: "Occupation",          type: "text",  icon: Briefcase },
  { key: "address",      label: "Address",             type: "text",  icon: MapPin },
];

export default function Profile() {
  const { user, loading, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [profile, setProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [form, setForm] = useState({});
  const [bio, setBio] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploadingPic, setUploadingPic] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/login", { replace: true });
  }, [loading, user, navigate]);

  const loadProfile = useCallback(async () => {
    if (!user) return;
    setLoadingProfile(true);
    try {
      const { data } = await api.get("/profile/me");
      setProfile(data);
      setForm({
        name:          data.name || "",
        phone:         data.phone || "",
        country:       data.country || "",
        nationality:   data.nationality || "",
        date_of_birth: data.date_of_birth || "",
        occupation:    data.occupation || "",
        address:       data.address || "",
      });
      setBio(data.bio || "");
      setPreviewUrl(data.picture || null);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoadingProfile(false);
    }
  }, [user]);

  useEffect(() => { if (user) loadProfile(); }, [user, loadProfile]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name?.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      await api.patch("/profile/me", { ...form, bio });
      toast.success("Profile updated successfully");
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      await refresh();
      loadProfile();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const handlePictureChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp", "image/gif"].includes(file.type)) {
      toast.error("Please select a JPEG, PNG, WebP or GIF image");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5 MB");
      return;
    }
    setUploadingPic(true);
    try {
      const reader = new FileReader();
      reader.onload = async (ev) => {
        const dataUrl = ev.target.result;
        // Extract base64 part
        const [meta, base64] = dataUrl.split(",");
        const contentType = meta.match(/data:(.*);base64/)?.[1] || file.type;
        try {
          const { data } = await api.post("/profile/me/picture", {
            image_data: base64,
            content_type: contentType,
          });
          setPreviewUrl(data.picture);
          await refresh();
          toast.success("Profile picture updated");
        } catch (err) {
          toast.error(errMsg(err));
        } finally {
          setUploadingPic(false);
        }
      };
      reader.onerror = () => { toast.error("Failed to read image"); setUploadingPic(false); };
      reader.readAsDataURL(file);
    } catch (err) {
      toast.error(errMsg(err));
      setUploadingPic(false);
    }
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen grid place-items-center bg-[hsl(var(--zt-beige))]">
        <Loader2 className="animate-spin text-[hsl(var(--zt-teal))]" size={28} />
      </div>
    );
  }

  const initials = ((profile?.name || user?.name || user?.email || "U")
    .split(" ")
    .filter(Boolean)
    .map(w => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2)) || "U";

  return (
    <div className="min-h-screen bg-[hsl(var(--zt-beige))] pb-24">
      {/* Header */}
      <div className="bg-[hsl(var(--zt-teal))] text-white relative overflow-hidden">
        <div aria-hidden className="absolute -top-40 -right-40 w-[450px] h-[450px] rounded-full opacity-[0.15] blur-3xl pointer-events-none"
          style={{ background: "radial-gradient(circle, #4F63F0 0%, transparent 60%)" }} />
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 h-24 flex items-center justify-between relative z-10">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate("/")} className="transition-transform hover:scale-[1.02]">
              <img src="/zenstrust-logo.png" alt="Zenstrust" className="h-14 w-auto rounded-md object-contain" />
            </button>
            <span className="hidden sm:inline font-heading uppercase tracking-[0.25em] text-[10px] text-[hsl(var(--zt-gold))] font-bold border-l border-white/15 pl-4">
              My Profile
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => navigate("/dashboard")}
              className="text-white hover:bg-white/10 font-heading uppercase tracking-[0.16em] text-xs h-10 px-4 rounded-full border border-white/10 hidden sm:flex">
              Dashboard
            </Button>
            <Button variant="ghost" onClick={async () => { await logout(); navigate("/"); }}
              className="text-white hover:bg-white/10 font-heading uppercase tracking-[0.16em] text-xs h-10 px-5 rounded-full border border-white/10">
              <LogOut size={15} className="mr-2" /> Log out
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-[1000px] mx-auto px-6 md:px-12 py-12 space-y-8">
        <div>
          <h1 className="font-display text-3xl lg:text-4xl text-[hsl(var(--zt-teal))] font-bold">
            Your Profile
          </h1>
          <p className="text-sm text-[hsl(var(--zt-charcoal))]/60 mt-1 font-body">
            Manage your personal information and account details.
          </p>
        </div>

        {loadingProfile ? (
          <div className="grid place-items-center py-20">
            <Loader2 className="animate-spin text-[hsl(var(--zt-teal))]" size={28} />
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Left — Avatar + read-only info */}
            <div className="space-y-5">
              {/* Avatar card */}
              <div className="bg-white rounded-2xl border border-[hsl(var(--zt-beige-deep))] p-6 shadow-sm flex flex-col items-center gap-4">
                <div className="relative group">
                  <div className="w-24 h-24 rounded-2xl overflow-hidden bg-[hsl(var(--zt-teal))] flex items-center justify-center shadow-md">
                    {previewUrl ? (
                      <img src={previewUrl} alt="Profile" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-white text-3xl font-bold font-display">{initials}</span>
                    )}
                  </div>
                  <button
                    onClick={() => fileRef.current?.click()}
                    disabled={uploadingPic}
                    aria-label="Change profile picture"
                    className="absolute -bottom-2 -right-2 w-8 h-8 rounded-full bg-[hsl(var(--zt-gold))] text-white grid place-items-center shadow-md hover:bg-[hsl(var(--zt-gold))]/90 transition-colors disabled:opacity-60"
                  >
                    {uploadingPic ? <Loader2 size={13} className="animate-spin" /> : <Camera size={13} />}
                  </button>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    className="hidden"
                    onChange={handlePictureChange}
                  />
                </div>
                <div className="text-center">
                  <p className="font-display text-lg text-[hsl(var(--zt-teal))] font-bold">
                    {profile?.name || "—"}
                  </p>
                  <p className="text-xs text-[hsl(var(--zt-charcoal))]/55 font-body mt-0.5">
                    {profile?.email}
                  </p>
                </div>
                <p className="text-[10px] font-heading uppercase tracking-wider text-[hsl(var(--zt-charcoal))]/40 text-center">
                  Click the camera icon to update your photo
                </p>
              </div>

              {/* Account info (read-only) */}
              <div className="bg-white rounded-2xl border border-[hsl(var(--zt-beige-deep))] p-5 shadow-sm space-y-3">
                <p className="font-heading uppercase tracking-[0.18em] text-[10px] text-[hsl(var(--zt-teal))]/60 font-bold mb-1">Account Info</p>
                <InfoRow icon={Mail} label="Email" value={profile?.email} />
                <InfoRow icon={Shield} label="Sign-in" value={
                  profile?.auth_provider === "google" ? "Google" :
                  profile?.auth_provider === "both" ? "Email + Google" : "Email"
                } />
                <InfoRow icon={Calendar} label="Member since" value={
                  profile?.created_at
                    ? new Date(profile.created_at).toLocaleDateString(undefined, { month: "long", year: "numeric" })
                    : "—"
                } />
                <div className="pt-1">
                  <span className={`inline-flex items-center gap-1.5 text-[9px] font-heading uppercase tracking-wider px-2.5 py-1 rounded-full font-bold border ${
                    profile?.is_active
                      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                      : "bg-red-50 text-red-500 border-red-200"
                  }`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${profile?.is_active ? "bg-emerald-500" : "bg-red-400"}`} />
                    {profile?.is_active ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>
            </div>

            {/* Right — Edit form */}
            <div className="lg:col-span-2">
              <form onSubmit={handleSave} className="bg-white rounded-2xl border border-[hsl(var(--zt-beige-deep))] p-6 shadow-sm space-y-5">
                <div className="flex items-center justify-between mb-1">
                  <h2 className="font-display text-lg text-[hsl(var(--zt-teal))] font-bold">Edit Information</h2>
                  {saved && (
                    <span className="flex items-center gap-1.5 text-emerald-600 text-xs font-heading uppercase tracking-wider">
                      <Check size={13} /> Saved
                    </span>
                  )}
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  {FIELD_DEFS.map(f => (
                    <div key={f.key}>
                      <label htmlFor={`profile-${f.key}`} className="block text-[10px] font-heading uppercase tracking-wider text-[hsl(var(--zt-charcoal))]/55 mb-1.5">
                        {f.label}{f.required && <span className="text-red-400 ml-0.5">*</span>}
                      </label>
                      <div className="relative">
                        <f.icon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--zt-charcoal))]/35 pointer-events-none" />
                        <input
                          id={`profile-${f.key}`}
                          type={f.type}
                          value={form[f.key] || ""}
                          onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                          required={f.required}
                          className="w-full h-10 pl-9 pr-3 text-sm border border-[hsl(var(--zt-beige-deep))] rounded-xl focus:outline-none focus:ring-2 focus:ring-[hsl(var(--zt-teal))]/20 focus:border-[hsl(var(--zt-teal))]/40 bg-[hsl(var(--zt-beige))]/30 transition-colors"
                        />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Bio */}
                <div>
                  <label htmlFor="profile-bio" className="block text-[10px] font-heading uppercase tracking-wider text-[hsl(var(--zt-charcoal))]/55 mb-1.5">
                    <FileText size={13} className="inline mr-1.5 -mt-0.5" />About / Bio
                  </label>
                  <textarea
                    id="profile-bio"
                    value={bio}
                    onChange={e => setBio(e.target.value)}
                    rows={3}
                    maxLength={500}
                    placeholder="Tell us about yourself…"
                    className="w-full px-3 py-2.5 text-sm border border-[hsl(var(--zt-beige-deep))] rounded-xl focus:outline-none focus:ring-2 focus:ring-[hsl(var(--zt-teal))]/20 focus:border-[hsl(var(--zt-teal))]/40 bg-[hsl(var(--zt-beige))]/30 resize-none transition-colors"
                  />
                  <p className="text-[10px] text-[hsl(var(--zt-charcoal))]/40 mt-1 text-right">{bio.length}/500</p>
                </div>

                <div className="flex items-center justify-end pt-2 border-t border-[hsl(var(--zt-beige-deep))]">
                  <Button
                    type="submit"
                    disabled={saving}
                    className="bg-[hsl(var(--zt-teal))] text-white hover:bg-[hsl(var(--zt-teal))]/90 rounded-full h-10 px-8 font-heading uppercase tracking-wider text-xs font-bold"
                  >
                    {saving ? (
                      <><Loader2 size={14} className="animate-spin mr-2" /> Saving…</>
                    ) : (
                      <><Save size={14} className="mr-2" /> Save Changes</>
                    )}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3">
      <Icon size={13} className="text-[hsl(var(--zt-gold))] flex-shrink-0" />
      <span className="text-[10px] font-heading uppercase tracking-wider text-[hsl(var(--zt-charcoal))]/45 w-20 flex-shrink-0">{label}</span>
      <span className="text-sm text-[hsl(var(--zt-charcoal))]/80 font-semibold truncate">{value || "—"}</span>
    </div>
  );
}
